import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"

import getAppConfig from "~/utils/getAppConfig"
import { apiLineItems } from "~/routes"

import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"

import * as Yup from "yup"
import FormikWrapper from "~/library/FormikWrapper"
import FormikInput from "~/library/FormikInput"
import FormikTextarea from "~/library/FormikTextarea"
import CurrencyInput from "~/library/CurrencyInput"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchModal from "~/library/CatchModal"
import { Field } from "formik"
import useFetchResource from "~/hooks/useFetchResource"
import Pagination from "~/library/Pagination"
import NavigationLink from "~/library/NavigationLink"
import { formatCurrencyCents } from "~/utils/helpers"

interface PackItem {
  id?: number
  tempId?: string // For new items without database ID
  lineItemId: string
  name: string
  amountCents: number
  quantity: number
  _destroy?: boolean
}

// Helper function to get a consistent identifier for pack items
const getItemId = (item: PackItem): string | number => {
  return item.id || item.tempId || ""
}

interface PackFormProps {
  onSubmit: (values: any) => void
  initialValues?: any
  isEditing?: boolean
}

const packSchema = Yup.object({
  name: Yup.string().required("Pack name is required"),
  priceCents: Yup.number().min(0, "Price must be positive").required("Price is required"),
  active: Yup.boolean(),
})

const PackForm: React.FC<PackFormProps> = ({ onSubmit, initialValues, isEditing = false }) => {
  const { venueSlug } = getAppConfig()

  const [showLineItemModal, setShowLineItemModal] = useState(false)
  const [selectedLineItems, setSelectedLineItems] = useState<PackItem[]>([])

  const scopeParams = {
    "q[active_eq]": "true",
  }

  const {
    data: availableLineItems,
    refetch,
    metadata,
    isLoading: isLineItemsLoading,
  } = useFetchResource<{ lineItems: any[] }, "lineItems">(
    apiLineItems.index.path({ venueSlug }),
    "lineItems",
    { page: "1", ...scopeParams }
  )

  const handlePageChange = (page: string) => {
    refetch({ page: page, ...scopeParams })
  }

  useEffect(() => {
    if (isEditing && initialValues?.items) {
      setSelectedLineItems(
        initialValues.items.map((item: any) => ({
          id: item.id,
          name: item.lineItem.name,
          amountCents: item.lineItem.amountCents,
          quantity: item.quantity,
          lineItemId: item.lineItemId,
        }))
      )
    }
  }, [initialValues])

  const defaultValues = {
    name: "",
    description: "",
    priceCents: 0,
    active: true,
    packItems: [],
  }

  const handleAddLineItem = (lineItem: any) => {
    const exists = selectedLineItems.find((item) => item.lineItemId === lineItem.uuid)
    if (!exists) {
      setSelectedLineItems([
        ...selectedLineItems,
        {
          tempId: `temp_${Date.now()}_${Math.random()}`, // Unique temporary ID
          lineItemId: lineItem.uuid,
          name: lineItem.name,
          amountCents: lineItem.amountCents,
          quantity: 1,
        },
      ])
    }
  }

  const handleRemoveLineItem = (itemIdentifier: string | number | undefined) => {
    if (!itemIdentifier) return
    setSelectedLineItems(
      selectedLineItems.map((item) =>
        getItemId(item) === itemIdentifier ? { ...item, _destroy: true } : item
      )
    )
  }

  // Filter out items marked for destruction for display
  const displayLineItems = selectedLineItems.filter((item) => !item._destroy)

  const handleUpdateQuantity = (itemIdentifier: string | number | undefined, quantity: string) => {
    if (!itemIdentifier) return
    setSelectedLineItems(
      selectedLineItems.map((item) =>
        getItemId(item) === itemIdentifier ? { ...item, quantity: parseInt(quantity) || 1 } : item
      )
    )
  }

  const handleFormSubmit = (values: any) => {
    const formData = {
      name: values.name,
      description: values.description,
      priceCents: values.priceCents,
      active: values.active,
      packItemsAttributes: selectedLineItems.map((item) => ({
        ...(item.id && { id: item.id }), // Only include ID if it exists (for existing items)
        lineItemId: item.lineItemId,
        quantity: item.quantity,
        _destroy: item._destroy,
      })),
    }
    onSubmit(formData)
  }

  return (
    <FormikWrapper
      onSubmit={handleFormSubmit}
      validationSchema={packSchema}
      initialValues={isEditing ? initialValues : defaultValues}
    >
      <h4>Pack Details</h4>
      <CatchCard>
        <FormikInput
          name="name"
          label="Pack Name"
          placeholder="Basic Suite package"
          required
          helpText=""
        />

        <FormikTextarea
          name="description"
          label="Description"
          placeholder="Describe what's included in this pack..."
          helpText=""
        />

        <CurrencyInput name="priceCents" label="Price" placeholder="99.99" currency="USD" />

        <div className="mb-3">
          <Field name="active">
            {({ field }: any) => (
              <div className="form-check">
                <input
                  {...field}
                  type="checkbox"
                  className="form-check-input"
                  id="active"
                  checked={field.value}
                />
                <label htmlFor="active" className="form-check-label">
                  Active
                </label>
                <div className="form-text">
                  When active, this pack will be available for booking
                </div>
              </div>
            )}
          </Field>
        </div>
      </CatchCard>

      <h4>Pack Items</h4>
      <CatchCard>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span>Selected Line Items</span>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setShowLineItemModal(true)}
          >
            Add Line Item
          </button>
        </div>

        {displayLineItems.length === 0 ? (
          <div className="text-center p-3 text-muted">
            No line items selected. Click &quot;Add Line Item&quot; to add items to this pack.
          </div>
        ) : (
          <CatchTable
            data={displayLineItems}
            columns={[
              {
                key: "name",
                header: "Line Item",
                formatter: (name: string) => name,
              },
              {
                key: "amountCents",
                header: "Unit Price",
                formatter: (amountCents: number) => formatCurrencyCents(amountCents),
              },
              {
                key: "quantity",
                header: "Quantity",
                formatter: (quantity: number, data: PackItem) => (
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => handleUpdateQuantity(getItemId(data), e.target.value)}
                    className="form-control"
                    style={{ width: "80px" }}
                  />
                ),
              },
              {
                key: "subtotal" as keyof PackItem,
                header: "Subtotal",
                formatter: (_, data: PackItem) =>
                  formatCurrencyCents(data.amountCents * data.quantity),
              },
              {
                key: "actions" as keyof PackItem,
                header: "Actions",
                formatter: (_, data: PackItem) => (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRemoveLineItem(getItemId(data))}
                  >
                    &#x2715;
                  </button>
                ),
              },
            ]}
          />
        )}
      </CatchCard>

      <div className="d-flex">
        <div className="ms-auto">
          <Link className="btn btn-secondary me-2" to="/packs">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary">
            {isEditing ? "Update Pack" : "Create Pack"}
          </button>
        </div>
      </div>

      <CatchModal
        isOpen={showLineItemModal}
        onClose={() => setShowLineItemModal(false)}
        title="Add Line Item"
        size="lg"
      >
        <LoadingWrapper isLoading={isLineItemsLoading}>
          {Array.isArray(availableLineItems) && availableLineItems.length === 0 ? (
            <div className="text-center p-3">
              <p>No line items available.</p>
              <Link to="/line-items/new" className="btn btn-primary">
                Create Line Item
              </Link>
            </div>
          ) : (
            <CatchCard>
              <CatchTable
                data={Array.isArray(availableLineItems) ? availableLineItems : []}
                columns={[
                  {
                    key: "name",
                    header: "Name",
                    formatter: (name, data) => (
                      <NavigationLink type="line-items" to={`/line-items/${data.id}`}>
                        {name}
                      </NavigationLink>
                    ),
                  },
                  {
                    key: "categoryName",
                    header: "Category",
                    formatter: (category: string) => category || "—",
                  },
                  {
                    key: "amountCents",
                    header: "Amount",
                    formatter: (amountCents: number) => formatCurrencyCents(amountCents),
                  },
                  {
                    key: "actions",
                    header: "Actions",
                    formatter: (_, data: any) => (
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => handleAddLineItem(data)}
                        disabled={selectedLineItems.some((item) => item.lineItemId === data.uuid)}
                      >
                        {selectedLineItems.some((item) => item.lineItemId === data.uuid) ? (
                          <span>&#x2713;</span>
                        ) : (
                          <span>+</span>
                        )}
                      </button>
                    ),
                  },
                ]}
              />
              {metadata && (
                <Pagination
                  metadata={metadata}
                  onPageChange={(page: number) => handlePageChange(page.toString())}
                />
              )}
            </CatchCard>
          )}
        </LoadingWrapper>
      </CatchModal>
    </FormikWrapper>
  )
}

export default PackForm
